import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { artikelPublicService } from '../../services/artikelPublicService';
import {
    FaCalendarAlt,
    FaUser,
    FaImage,
    FaSpinner,
    FaStar,
    FaMapPin,
    FaArrowRight,
    FaExclamationTriangle
} from 'react-icons/fa';

const BeritaTerkini = () => {
    const [beritaTerkini, setBeritaTerkini] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadBerita();
    }, []);

    const loadBerita = async () => {
        setLoading(true);
        setError('');

        const result = await artikelPublicService.getArtikels({
            kategori: 'berita',
            per_page: 3,
        });

        if (result.success) {
            const data = result.data.data || result.data || [];
            setBeritaTerkini(Array.isArray(data) ? data.slice(0, 3) : []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return path;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    };

    const truncateText = (text, maxLength = 150) => {
        if (!text) return '';
        return text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text;
    };

    // ============ LOADING STATE ============
    if (loading) {
        return (
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Berita Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                        <p className="text-gray-500">Memuat berita...</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ ERROR STATE ============
    if (error) {
        return (
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Berita Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">{error}</p>
                        <button onClick={loadBerita} className="btn-primary">
                            Coba Lagi
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    // ============ EMPTY STATE ============
    if (beritaTerkini.length === 0) {
        return (
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="section-title">Berita Terkini BPKAD</h2>
                    <div className="text-center py-12">
                        <FaImage className="text-6xl text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Belum ada berita terkini</p>
                    </div>
                </div>
            </section>
        );
    }

    // ============ BERITA LIST ============
    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="section-title !mb-0 !text-left">Berita Terkini BPKAD</h2>
                    <Link
                        to="/informasi/artikel/berita"
                        className="hidden md:flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold transition-colors"
                    >
                        Lihat Semua
                        <FaArrowRight />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {beritaTerkini.map((berita) => (
                        <article
                            key={berita.id}
                            className="card-glass overflow-hidden hover:shadow-xl transition-all duration-300 group flex flex-col"
                        >
                            {/* Thumbnail */}
                            <div className="relative overflow-hidden h-48 bg-gray-100">
                                {berita.thumbnail || berita.gambar ? (
                                    <img
                                        src={getImageUrl(berita.thumbnail || berita.gambar)}
                                        alt={berita.judul}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        onError={(e) => {
                                            e.target.src = 'https://placehold.co/400x300/0F4C3A/FFFFFF?text=Berita';
                                        }}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] flex items-center justify-center">
                                        <FaImage className="text-5xl text-white/50" />
                                    </div>
                                )}

                                {/* Badges */}
                                <div className="absolute top-2 left-2 flex gap-1">
                                    {berita.is_featured && (
                                        <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                            <FaStar className="mr-1" /> Featured
                                        </span>
                                    )}
                                    {berita.is_pinned && (
                                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full flex items-center shadow">
                                            <FaMapPin className="mr-1" /> Pinned
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 flex flex-col flex-1">
                                {/* Meta */}
                                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                                    <span className="flex items-center">
                                        <FaCalendarAlt className="mr-1 text-[#2E7D32]" />
                                        {formatDate(berita.published_at || berita.created_at)}
                                    </span>
                                    <span className="flex items-center">
                                        <FaUser className="mr-1 text-[#2E7D32]" />
                                        {berita.author}
                                    </span>
                                </div>

                                {/* Kategori */}
                                <span className="inline-block bg-[#2E7D32]/10 text-[#2E7D32] text-xs px-2 py-1 rounded-full mb-3 self-start capitalize">
                                    {berita.kategori}
                                </span>

                                {/* Judul */}
                                <h3 className="text-lg font-bold text-[#0F4C3A] mb-3 group-hover:text-[#2E7D32] transition-colors line-clamp-2">
                                    {berita.judul}
                                </h3>

                                {/* Summary */}
                                <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
                                    {berita.ringkasan
                                        ? truncateText(stripHtml(berita.ringkasan), 150)
                                        : truncateText(stripHtml(berita.konten), 150)}
                                </p>

                                {/* Read More */}
                                <Link
                                    to={`/informasi/artikel/detail/${berita.id}/${berita.slug}`}
                                    className="text-[#2E7D32] hover:text-[#4CAF50] font-semibold flex items-center gap-2 mt-auto transition-colors"
                                >
                                    Baca Selengkapnya
                                    <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Lihat Semua - Mobile */}
                <div className="text-center mt-8 md:hidden">
                    <Link
                        to="/informasi/artikel/berita"
                        className="inline-flex items-center gap-2 text-[#2E7D32] hover:text-[#4CAF50] font-semibold"
                    >
                        Lihat Semua Berita
                        <FaArrowRight />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BeritaTerkini;